import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './utils/supabase';
import { DEFAULT_B2C_COHORT_ID, AUTO_ADVANCE_DELAY } from './utils/constants';
import { saveProgress as persistProgress, loadSavedProgress, clearSavedProgress } from './utils/storage';
import Header from './components/Header';
import WelcomeScreen from './components/screens/WelcomeScreen';
import GuidanceScreen from './components/screens/GuidanceScreen';
import IdentityScreen from './components/screens/IdentityScreen';
import DemographicsScreen from './components/screens/DemographicsScreen';
import SurveyScreen from './components/screens/SurveyScreen';
import PerspectiveShiftScreen from './components/screens/PerspectiveShiftScreen';
import ReadyScreen from './components/screens/ReadyScreen';
import SuccessScreen from './components/screens/SuccessScreen';
import LoadingScreen from './components/screens/LoadingScreen';
import ErrorScreen from './components/screens/ErrorScreen';

var _E78 = true;
var _SURVEY_DUPCHECK = true;

function App() {
  const [currentScreen, setCurrentScreen] = useState('loading');
  const [questions, setQuestions] = useState([]);
  const [demographicItems, setDemographicItems] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [demographics, setDemographics] = useState({});
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submissionId, setSubmissionId] = useState('');
  const [cohortId, setCohortId] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [savedProgress, setSavedProgress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [jwtToken, setJwtToken] = useState('');
  const [theme, setTheme] = useState('dark');
  const [perspectiveDimension, setPerspectiveDimension] = useState('');
  const [perspectiveTargetIndex, setPerspectiveTargetIndex] = useState(null);
  const [insightsUrl, setInsightsUrl] = useState('');
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showReturningUserModal, setShowReturningUserModal] = useState(false);
  const [returningUserModalType, setReturningUserModalType] = useState('');
  const [returningUserAuthenticated, setReturningUserAuthenticated] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');
  const [submissionState, setSubmissionState] = useState('idle');
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  const questionStartTime = useRef(null);
  const instrumentVersion = useRef('');
  const insightsPortalBaseUrl = useRef('');
  const advanceTimer = useRef(null);
  const selectedRef = useRef(null);

  // --- Fetch instrument data on mount ---
  const fetchInstrument = useCallback(async () => {
    setCurrentScreen('loading');
    try {
      const { data: json, error: fnError } = await supabase.functions.invoke('get-instrument', { method: 'GET' });
      if (fnError) throw new Error('Failed to load instrument');
      if (!json || !json.success) throw new Error('Invalid instrument response');

      const data = json.data;
      instrumentVersion.current = data.version;
      insightsPortalBaseUrl.current = data.insights_portal_url || process.env.REACT_APP_INSIGHTS_URL || 'https://insights.ai-explorer.com.au';

      // Split demographics: D1-D3 for identity, D4+ for demographics screen
      const identityIds = new Set(['D1', 'D2', 'D3']);
      const demoItems = (data.demographic_items || []).filter(
        item => !identityIds.has(item.item_id)
      );
      setDemographicItems(demoItems);

      // Merge assessment + validity items, sort by position
      const allItems = [
        ...(data.assessment_items || []).map(q => ({ ...q, type: 'assessment' })),
        ...(data.validity_items || []).map(q => ({ ...q, dimension_id: null, type: 'validity' })),
      ].sort((a, b) => a.position - b.position);
      setQuestions(allItems);

      // Check URL for cohort_id — URL param always wins (B2B links)
      const urlParams = new URLSearchParams(window.location.search);
      const urlCohort = urlParams.get('cohort_id');
      const defaultCohort = data.default_cohort_key || DEFAULT_B2C_COHORT_ID;
      setCohortId(urlCohort || defaultCohort);

      // Check for saved progress
      const saved = loadSavedProgress(data.version);
      if (saved) {
        setSavedProgress(saved);
      }

      setCurrentScreen('s-welcome');
    } catch (err) {
      console.error('Instrument fetch error:', err);
      setCurrentScreen('error');
    }
  }, []);

  useEffect(() => {
    fetchInstrument();
  }, [fetchInstrument]);


  // --- Magic link return detection (runs on mount BEFORE screens) ---
  useEffect(() => {
    // Strip auth error hashes from URL
    if (window.location.hash && window.location.hash.includes('error=')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Check for magic link pending flag in localStorage
    const magicLinkPending = localStorage.getItem('ai-navigator-magic-link-pending');
    if (magicLinkPending) {
      try {
        const pendingData = JSON.parse(magicLinkPending);
        const pendingAge = Date.now() - new Date(pendingData.savedAt).getTime();
        if (pendingAge < 10 * 60 * 1000) {
          window.__returningFromMagicLink = true;
          window.__returningEmail = pendingData.email;
        } else {
          localStorage.removeItem('ai-navigator-magic-link-pending');
        }
      } catch (e) {
        localStorage.removeItem('ai-navigator-magic-link-pending');
      }
    }
  }, []);

  // --- Auth state listener (for magic link callback) ---
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.access_token) {
        window.__existingUserSession = session;
        setReturningUserAuthenticated(true);
        setShowReturningUserModal(false);
        setMagicLinkSent(false);

        // If returning from a magic link page reload, auto-restore progress
        if (window.__returningFromMagicLink) {
          localStorage.removeItem('ai-navigator-magic-link-pending');
          window.__returningFromMagicLink = false;

          const savedProgressStr = localStorage.getItem('ai-navigator-survey-progress');
          if (savedProgressStr) {
            try {
              const progress = JSON.parse(savedProgressStr);
              // Use the same resume logic as the Welcome screen Resume button
              setSubmissionId(progress.submissionId || crypto.randomUUID());
              setStartedAt(progress.startedAt || new Date().toISOString());
              if (progress.cohortId) setCohortId(progress.cohortId);
              setFirstName(progress.identity?.firstName || '');
              setLastName(progress.identity?.lastName || '');
              setEmail(progress.identity?.email || '');
              setDemographics(progress.demographics || {});
              setResponses(progress.responses || []);
              setCurrentQuestionIndex(progress.currentQuestion || 0);
              navigateTo(progress.currentScreen || 's-identity');
              return;
            } catch (e) {
              console.warn('Failed to restore progress after magic link:', e);
            }
          }
        }

        // Auto-retry submission if waiting for magic link on submit screen
        if (submissionState === 'awaiting_magic_link') {
          retrySubmissionWithToken(session.access_token);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [submissionState]); // eslint-disable-line

  // --- Compute progress percentage ---
  const progressPercent = (() => {
    if (currentScreen === 's-survey' || currentScreen === 's-insight') {
      return questions.length > 0 ? (responses.length / questions.length) * 100 : 0;
    }
    if (currentScreen === 's-ready' || currentScreen === 's-success') return 100;
    return 0;
  })();

  // --- Save progress helper ---
  const doSaveProgress = useCallback(() => {
    persistProgress({
      version: instrumentVersion.current,
      submissionId,
      cohortId,
      startedAt,
      currentScreen,
      currentQuestion: currentQuestionIndex,
      identity: { firstName, lastName, email },
      demographics,
      responses,
    });
  }, [submissionId, cohortId, startedAt, currentScreen, currentQuestionIndex, firstName, lastName, email, demographics, responses]);

  // --- Start timer for question ---
  useEffect(() => {
    if (currentScreen === 's-survey') {
      questionStartTime.current = performance.now();
      setSelectedIndex(null);
      selectedRef.current = null;
    }
  }, [currentScreen, currentQuestionIndex]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;

      if (currentScreen === 's-survey' && e.key >= '1' && e.key <= '4') {
        e.preventDefault();
        e.stopPropagation();
        const keyIndex = parseInt(e.key) - 1;
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ || !currentQ.response_options[keyIndex]) return;
        selectAnswer(currentQ.response_options[keyIndex].index);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const activeView = document.querySelector('.view.on');
        if (activeView) {
          const primaryBtn = activeView.querySelector('.btn.btn-p');
          if (primaryBtn && !primaryBtn.disabled) primaryBtn.click();
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentScreen, currentQuestionIndex, questions, responses, selectedIndex]); // eslint-disable-line -- selectAnswer uses ref

  // --- Navigation ---
  function navigateTo(screenId) {
    setCurrentScreen(screenId);
  }

  function goBack() {
    switch (currentScreen) {
      case 's-guidance':
        navigateTo('s-welcome');
        break;
      case 's-identity':
        navigateTo('s-guidance');
        break;
      case 's-demo':
        navigateTo('s-identity');
        break;
      case 's-survey':
        if (currentQuestionIndex > 0) {
          setCurrentQuestionIndex(prev => prev - 1);
        } else {
          navigateTo('s-demo');
        }
        break;
      case 's-insight':
        navigateTo('s-survey');
        break;
      case 's-ready':
        navigateTo('s-survey');
        break;
      default:
        break;
    }
  }

  // --- Start assessment ---
  function startAssessment() {
    setSubmissionId(crypto.randomUUID());
    setStartedAt(new Date().toISOString());
    navigateTo('s-guidance');
  }

  // --- Resume saved progress ---
  function resumeAssessment() {
    if (!savedProgress) return;
    setSubmissionId(savedProgress.submissionId || crypto.randomUUID());
    setStartedAt(savedProgress.startedAt || new Date().toISOString());
    setCohortId(savedProgress.cohortId || cohortId);
    setFirstName(savedProgress.identity?.firstName || '');
    setLastName(savedProgress.identity?.lastName || '');
    setEmail(savedProgress.identity?.email || '');
    setDemographics(savedProgress.demographics || {});
    setResponses(savedProgress.responses || []);
    setCurrentQuestionIndex(savedProgress.currentQuestion || 0);
    navigateTo(savedProgress.currentScreen || 's-welcome');
    setSavedProgress(null);
  }

  function startFresh() {
    clearSavedProgress();
    setSavedProgress(null);
    startAssessment();
  }

  // --- Effective dimension lookup (handles validity items with no dimension_id) ---
  function getEffectiveDimension(idx) {
    for (let i = idx; i >= 0; i--) {
      if (questions[i].dimension_id) return questions[i].dimension_id;
    }
    return null;
  }

  function getNextEffectiveDimension(idx) {
    for (let i = idx; i < questions.length; i++) {
      if (questions[i].dimension_id) return questions[i].dimension_id;
    }
    return null;
  }

  // --- Select survey answer ---
  function selectAnswer(optionIndex) {
    if (selectedRef.current !== null) return; // Prevent double-click (use ref for fresh value)

    const question = questions[currentQuestionIndex];
    if (!question) return;
    if (optionIndex < 0 || optionIndex >= question.response_options.length) return;

    setSelectedIndex(optionIndex);
    selectedRef.current = optionIndex;

    const responseTimeMs = questionStartTime.current
      ? Math.round(performance.now() - questionStartTime.current)
      : 0;

    const newResponse = {
      item_id: question.item_id,
      option_index: optionIndex,
      response_time_ms: responseTimeMs,
    };

    // Update or add response
    setResponses(prev => {
      const existing = prev.findIndex(r => r.item_id === question.item_id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = newResponse;
        return updated;
      }
      return [...prev, newResponse];
    });

    // Auto-advance after delay
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      const nextIndex = currentQuestionIndex + 1;

      if (nextIndex >= questions.length) {
        doSaveProgress();
        navigateTo('s-ready');
        return;
      }

      const next = questions[nextIndex];

      // Show Perspective Shift for UPCOMING dimension
      if (next.dimension_id) {
        let lastDimension = null;
        for (let i = currentQuestionIndex; i >= 0; i--) {
          if (questions[i].dimension_id) {
            lastDimension = questions[i].dimension_id;
            break;
          }
        }
        if (!lastDimension || lastDimension !== next.dimension_id) {
          setPerspectiveDimension(next.dimension_id);
          setPerspectiveTargetIndex(nextIndex);
          doSaveProgress();
          navigateTo('s-insight');
          return;
        }
      }

      setCurrentQuestionIndex(nextIndex);
      doSaveProgress();
    }, AUTO_ADVANCE_DELAY);
  }

  // --- Rewind section ---
  function rewindSection() {
    if (!window.confirm('Rewind to the first question of this section? Your answers in this section will be cleared.')) return;

    const currentDim = getEffectiveDimension(currentQuestionIndex);
    if (!currentDim) return;

    let sectionStart = currentQuestionIndex;
    while (sectionStart > 0 && getEffectiveDimension(sectionStart - 1) === currentDim) {
      sectionStart--;
    }

    // Clear responses for this section
    const sectionItemIds = new Set();
    for (let i = sectionStart; i <= currentQuestionIndex; i++) {
      sectionItemIds.add(questions[i].item_id);
    }
    setResponses(prev => prev.filter(r => !sectionItemIds.has(r.item_id)));
    setCurrentQuestionIndex(sectionStart);
    setSelectedIndex(null);
  }

  // --- Set demographic ---
  function setDemographic(fieldName, value) {
    setDemographics(prev => ({ ...prev, [fieldName]: value }));
  }

  // --- Check email status for returning users ---
  async function checkEmailStatus() {
    if (!email.trim()) return true;
    // Skip if already authenticated from magic link
    if (window.__existingUserSession) return true;
    setEmailCheckLoading(true);
    try {
      const dupEmail = email.trim().toLowerCase();
      const { data: status, error } = await supabase.rpc('check_email_status', { check_email: dupEmail });
      if (!error && status === 'completed') {
        setEmailCheckLoading(false);
        // Expose insights URL for vanilla JS panel
        window.__insightsPortalBaseUrl = insightsPortalBaseUrl.current;
        // Show non-dismissible vanilla JS overlay (outside React)
        if (typeof window.showAlreadyCompletedPanel === 'function') {
          window.showAlreadyCompletedPanel(dupEmail);
        } else {
          // Fallback: use React modal
          setReturningUserModalType('completed');
          setShowReturningUserModal(true);
        }
        return false;
      } else if (!error && status === 'no_submission') {
        setReturningUserModalType('no_submission');
        setShowReturningUserModal(true);
        setEmailCheckLoading(false);
        return false;
      }
    } catch (e) {
      console.warn('[dup-check] check_email_status failed, proceeding anyway:', e);
    }
    setEmailCheckLoading(false);
    return true;
  }

  // --- Send magic link for returning users ---
  async function sendMagicLink() {
    setMagicLinkError('');
    try {
      // Save pending flag to localStorage (survives page reload)
      localStorage.setItem('ai-navigator-magic-link-pending', JSON.stringify({
        email: email.trim(),
        savedAt: new Date().toISOString(),
      }));
      // Save survey progress
      doSaveProgress();

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { shouldCreateUser: false, emailRedirectTo: window.location.origin + window.location.pathname },
      });
      if (otpError) {
        localStorage.removeItem('ai-navigator-magic-link-pending');
        setMagicLinkError('Too many attempts. Please wait a moment and try again.');
        return;
      }
      setMagicLinkSent(true);
    } catch (e) {
      localStorage.removeItem('ai-navigator-magic-link-pending');
      setMagicLinkError('Failed to send sign-in link. Please try again.');
    }
  }

  // --- Retry submission with a provided token ---
  async function retrySubmissionWithToken(token) {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmissionState('idle');
    try {
      const completedAt = new Date().toISOString();
      const payload = {
        submission_id: submissionId,
        cohort_id: cohortId,
        started_at: startedAt,
        completed_at: completedAt,
        responses: responses.map(r => ({
          item_id: r.item_id,
          option_index: r.option_index,
          response_time_ms: r.response_time_ms || null,
        })),
        demographics: { first_name: firstName, last_name: lastName, email, ...demographics },
      };
      const { data: result, error: fnError } = await supabase.functions.invoke('submission-ingestion', {
        body: payload,
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (fnError) throw new Error(fnError.message || 'Submission failed');
      if (result && !result.success) throw new Error(result.error?.message || result.error || 'Submission failed');
      clearSavedProgress();
      setJwtToken(token);
      const resultSubmissionId = (result && result.submission_id) || submissionId;
      const insightsBase = insightsPortalBaseUrl.current;
      const params = new URLSearchParams();
      params.set('submission_id', resultSubmissionId);
      params.set('token', token);
      setInsightsUrl(`${insightsBase}/?${params.toString()}`);
      navigateTo('s-success');
    } catch (err) {
      console.error('Retry submission error:', err);
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  // --- Submit assessment ---
  async function submitAssessment() {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Step 0: Check if user already has a session (from magic link / early check)
      let token = '';
      const existingSession = await supabase.auth.getSession();
      if (existingSession?.data?.session?.access_token) {
        token = existingSession.data.session.access_token;
      }

      // Step 1: If no session, try to create a new user
      if (!token) {
        const password = crypto.randomUUID();
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: email,
          password: password,
        });

        if (authError) {
          // Explicit error from signUp — fall through to OTP
          console.warn('signUp returned error, sending magic link:', authError.message);
        } else if (authData?.session?.access_token) {
          // New user — got session directly from signUp response (no getSession needed)
          token = authData.session.access_token;
        } else {
          // signUp returned null session with no error — duplicate email detected
          console.warn('signUp returned null session (duplicate email), sending magic link');
        }

        // If no token after signUp, fall back to OTP magic link
        if (!token) {
          doSaveProgress();
          localStorage.setItem('ai-navigator-magic-link-pending', JSON.stringify({
            email: email,
            savedAt: new Date().toISOString(),
          }));
          const { error: otpError } = await supabase.auth.signInWithOtp({
            email: email,
            options: { shouldCreateUser: false, emailRedirectTo: window.location.origin + window.location.pathname },
          });
          if (otpError) {
            localStorage.removeItem('ai-navigator-magic-link-pending');
            throw new Error('Could not send sign-in link. Please try again or use a different email.');
          }
          setSubmissionState('awaiting_magic_link');
          setIsSubmitting(false);
          return;
        }
      }

      if (!token) {
        throw new Error('No authentication token received. Please check the email address.');
      }

      // Step 2: Build payload
      const completedAt = new Date().toISOString();
      const payload = {
        submission_id: submissionId,
        cohort_id: cohortId,
        started_at: startedAt,
        completed_at: completedAt,
        responses: responses.map(r => ({
          item_id: r.item_id,
          option_index: r.option_index,
          response_time_ms: r.response_time_ms || null,
        })),
        demographics: {
          first_name: firstName,
          last_name: lastName,
          email: email,
          ...demographics,
        },
      };

      // Step 3: Submit using supabase.functions.invoke (handles apikey header automatically)
      const { data: result, error: fnError } = await supabase.functions.invoke('submission-ingestion', {
        body: payload,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (fnError) {
        throw new Error(fnError.message || 'Submission failed');
      }

      if (result && !result.success) {
        throw new Error(result.error?.message || result.error || 'Submission failed');
      }

      // Success — build insights URL and redirect
      clearSavedProgress();
      setJwtToken(token);
      const resultSubmissionId = (result && result.submission_id) || submissionId;
      const insightsBase = insightsPortalBaseUrl.current;
      const params = new URLSearchParams();
      params.set('submission_id', resultSubmissionId);
      if (token) params.set('token', token);
      const url = `${insightsBase}/?${params.toString()}`;
      console.log('[Submit] Success! Insights URL:', url);
      setInsightsUrl(url);
      navigateTo('s-success');
    } catch (err) {
      console.error('Submission error:', err);
      // Even on error, if we have a submissionId, build a partial URL so user can retry later
      if (!submitError) {
        const insightsBase = insightsPortalBaseUrl.current;
        setInsightsUrl(`${insightsBase}/?submission_id=${submissionId}`);
      }
      setSubmitError(err.message || 'Submission failed. Your answers are safe — please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // --- Theme toggle ---
  function toggleTheme() {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.dataset.theme = newTheme;
  }

  return (
    <div className="shell-narrow" style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}>
      <Header
        onToggleTheme={toggleTheme}
        theme={theme}
      />

      {progressPercent > 0 && (
        <div style={{
          width: '100%',
          height: '4px',
          background: 'var(--brd, rgba(255,255,255,0.08))',
          borderRadius: '2px',
          flexShrink: 0,
          margin: '16px 0',
          overflow: 'hidden',
        }} data-testid="progress-bar-track">
          <div style={{
            width: `${Math.max(progressPercent, 2)}%`,
            height: '100%',
            background: 'linear-gradient(90deg, var(--teal), var(--indigo, var(--teal)))',
            borderRadius: '2px',
            transition: 'width 0.4s ease',
            boxShadow: '0 0 8px rgba(45,212,191,0.4)',
          }} data-testid="progress-bar"></div>
        </div>
      )}

      {currentScreen === 'loading' && <LoadingScreen />}

      {currentScreen === 'error' && <ErrorScreen onRetry={fetchInstrument} />}

      {currentScreen === 's-welcome' && (
        <WelcomeScreen
          onBegin={startAssessment}
          savedProgress={savedProgress}
          onResume={resumeAssessment}
          onStartFresh={startFresh}
        />
      )}

      {currentScreen === 's-guidance' && (
        <GuidanceScreen
          onContinue={() => navigateTo('s-identity')}
          onBack={() => navigateTo('s-welcome')}
        />
      )}

      {currentScreen === 's-identity' && (
        <IdentityScreen
          firstName={firstName}
          lastName={lastName}
          email={email}
          setFirstName={setFirstName}
          setLastName={setLastName}
          setEmail={setEmail}
          onContinue={async () => {
            const canProceed = await checkEmailStatus();
            if (canProceed) {
              doSaveProgress();
              navigateTo('s-demo');
            }
          }}
          onBack={() => navigateTo('s-guidance')}
          isLoading={emailCheckLoading}
        />
      )}

      {currentScreen === 's-demo' && (
        <DemographicsScreen
          items={demographicItems}
          demographics={demographics}
          setDemographic={(field, value) => {
            setDemographic(field, value);
            setTimeout(doSaveProgress, 0);
          }}
          onContinue={() => {
            doSaveProgress();
            // Show Perspective Shift for first dimension before Q1
            const firstQ = questions[0];
            if (firstQ && firstQ.dimension_id) {
              setPerspectiveDimension(firstQ.dimension_id);
              setPerspectiveTargetIndex(0);
              navigateTo('s-insight');
            } else {
              setCurrentQuestionIndex(0);
              navigateTo('s-survey');
            }
          }}
          onBack={() => navigateTo('s-identity')}
          onOpenConsentModal={() => setShowConsentModal(true)}
        />
      )}

      {currentScreen === 's-survey' && questions[currentQuestionIndex] && (
        <SurveyScreen
          question={questions[currentQuestionIndex]}
          questionIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          selectedIndex={selectedIndex}
          onSelectAnswer={selectAnswer}
          onRewindSection={rewindSection}
          onBack={goBack}
          effectiveDimension={getEffectiveDimension(currentQuestionIndex)}
        />
      )}

      {currentScreen === 's-insight' && (
        <PerspectiveShiftScreen
          dimensionId={perspectiveDimension}
          onContinue={() => {
            setCurrentQuestionIndex(perspectiveTargetIndex);
            navigateTo('s-survey');
          }}
        />
      )}

      {currentScreen === 's-ready' && (
        <ReadyScreen
          onSubmit={submitAssessment}
          isSubmitting={isSubmitting}
          submitError={submitError}
          submissionState={submissionState}
          email={email}
          onRetrySubmission={submitAssessment}
        />
      )}

      {currentScreen === 's-success' && (
        <SuccessScreen insightsUrl={insightsUrl} totalQuestions={questions.length} />
      )}

      <footer style={{ textAlign: 'center', padding: '16px 0', marginTop: 'auto' }}>
        <p className="disclaimer" data-content="footer.copyright">
          2026 AI Navigator. All rights reserved.
        </p>
      </footer>

      {showConsentModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowConsentModal(false)}
          data-testid="consent-modal-overlay"
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-alt, var(--bg))', border: '1px solid var(--brd)',
              borderRadius: '20px', padding: '32px', maxWidth: '600px', width: '100%',
              maxHeight: '80vh', overflowY: 'auto',
            }}
            data-testid="consent-modal"
          >
            <h2 style={{ marginBottom: '16px', color: 'var(--tx)' }}>Privacy & Consent</h2>
            <div className="dim" data-content="_misc.consent_full_text" style={{ lineHeight: '1.7', whiteSpace: 'pre-line' }}>
              Your responses to this assessment will be used solely to generate your personalised AI readiness report. All data is stored securely and processed in accordance with our privacy policy.{'\n\n'}We collect: your name, email address, demographic information (industry, role, experience level), and your assessment responses.{'\n\n'}Your data will not be shared with third parties without your explicit consent. Aggregated, anonymised data may be used for benchmarking and research purposes.{'\n\n'}You may request deletion of your data at any time by contacting support@ai-explorer.com.au.
            </div>
            <button
              className="btn btn-p"
              onClick={() => setShowConsentModal(false)}
              style={{ marginTop: '24px', width: '100%' }}
              data-testid="consent-modal-close"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showReturningUserModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
          data-testid="returning-user-modal-overlay"
        >
          <div
            style={{
              background: 'var(--bg-alt, var(--bg))', border: '1px solid var(--brd)',
              borderRadius: '20px', padding: '32px', maxWidth: '420px', width: '100%',
              textAlign: 'center',
            }}
            data-testid="returning-user-modal"
          >
            {returningUserModalType === 'completed' && (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9989;</div>
                <h2 style={{ marginBottom: '12px', color: 'var(--tx)' }}>You've already completed your assessment!</h2>
                <p className="dim" style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  Your AI Navigator report is ready and waiting for you.
                </p>
                <p className="hint-text" style={{ marginBottom: '28px' }}>
                  Signed in as: {email}
                </p>
                <a
                  href={insightsPortalBaseUrl.current || 'https://insights.ai-explorer.com.au'}
                  className="btn btn-p"
                  style={{ display: 'block', width: '100%', textDecoration: 'none', textAlign: 'center' }}
                  data-testid="view-results-btn"
                >
                  View My Results &rarr;
                </a>
              </>
            )}

            {returningUserModalType === 'no_submission' && !magicLinkSent && (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9993;&#65039;</div>
                <h2 style={{ marginBottom: '12px', color: 'var(--tx)' }}>Welcome back!</h2>
                <p className="dim" style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  It looks like you started an assessment before with this email. To continue, we'll send a sign-in link to your email.
                </p>
                <p className="hint-text" style={{ marginBottom: '24px' }}>
                  This lets us securely link your answers to your account.
                </p>
                {magicLinkError && (
                  <p style={{ color: 'var(--amber)', fontSize: '0.85rem', marginBottom: '16px' }}>{magicLinkError}</p>
                )}
                <button
                  className="btn btn-p"
                  onClick={sendMagicLink}
                  style={{ width: '100%', marginBottom: '16px' }}
                  data-testid="send-magic-link-btn"
                >
                  Send Sign-In Link &rarr;
                </button>
                <button
                  className="text-link"
                  onClick={() => { setShowReturningUserModal(false); setEmail(''); setMagicLinkError(''); localStorage.removeItem('ai-navigator-magic-link-pending'); }}
                  data-testid="use-different-email-btn"
                >
                  Use a Different Email
                </button>
              </>
            )}

            {returningUserModalType === 'no_submission' && magicLinkSent && (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9993;&#65039;</div>
                <h2 style={{ marginBottom: '12px', color: 'var(--tx)' }}>Check your email</h2>
                <p className="dim" style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  We've sent a sign-in link to <strong style={{ color: 'var(--tx)' }}>{email}</strong>. Click the link in your email to continue.
                </p>
                <p className="hint-text" style={{ marginBottom: '24px' }}>
                  Once you've clicked the link, come back here — the survey will continue automatically.
                </p>
                {magicLinkError && (
                  <p style={{ color: 'var(--amber)', fontSize: '0.85rem', marginBottom: '16px' }}>{magicLinkError}</p>
                )}
                <button
                  className="text-link"
                  onClick={() => { setMagicLinkSent(false); sendMagicLink(); }}
                  style={{ marginBottom: '12px' }}
                  data-testid="resend-magic-link-btn"
                >
                  Resend Link
                </button>
                <br />
                <button
                  className="text-link"
                  onClick={() => { setShowReturningUserModal(false); setEmail(''); setMagicLinkSent(false); setMagicLinkError(''); localStorage.removeItem('ai-navigator-magic-link-pending'); }}
                  data-testid="use-different-email-btn"
                >
                  Use a Different Email
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
